export class ApiError extends Error {
  status: number;
  detail: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export type ApiRequestInit = RequestInit & { timeoutMs?: number };

export async function api<T>(path: string, options: ApiRequestInit = {}): Promise<T> {
  const { timeoutMs, signal: upstreamSignal, ...requestOptions } = options;
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const controller = timeoutMs ? new AbortController() : null;
  let didTimeout = false;
  const forwardAbort = () => controller?.abort(upstreamSignal?.reason);
  if (controller && upstreamSignal) {
    if (upstreamSignal.aborted) forwardAbort();
    else upstreamSignal.addEventListener("abort", forwardAbort, { once: true });
  }
  const timeout = controller ? window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, timeoutMs) : undefined;
  let response: Response;
  try {
    response = await fetch(path, {
      ...requestOptions,
      headers,
      credentials: "include",
      signal: controller?.signal ?? upstreamSignal,
    });
  } catch (error) {
    if (didTimeout) throw new ApiError(408, "请求超时，请检查网络后重试");
    throw error;
  } finally {
    if (timeout !== undefined) window.clearTimeout(timeout);
    upstreamSignal?.removeEventListener("abort", forwardAbort);
  }
  if (!response.ok) {
    let body: { detail?: unknown; error?: { message?: string } } | null = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    const detail = body?.detail;
    const message = body?.error?.message || (typeof detail === "string" ? detail : `请求失败（${response.status}）`);
    throw new ApiError(response.status, message, detail);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function jsonBody(value: unknown): RequestInit {
  return { body: JSON.stringify(value) };
}
